import { useEffect, useRef, useCallback } from 'react';

interface UseVoiceInterruptionOptions {
  enabled: boolean;
  isSpeaking: boolean;
  audioContext?: AudioContext | null;
  analyser?: AnalyserNode | null;
  onInterruption: () => void;
  threshold?: number; // Volume threshold for interruption (0-255)
  checkInterval?: number; // How often to check volume in ms
}

const DEFAULT_THRESHOLD = 50; // Default volume threshold
const DEFAULT_CHECK_INTERVAL = 100; // Check every 100ms
const CONSECUTIVE_THRESHOLD_COUNT = 3; // Need 3 consecutive loud samples to trigger

export const useVoiceInterruption = ({
  enabled,
  isSpeaking,
  audioContext,
  analyser,
  onInterruption,
  threshold = DEFAULT_THRESHOLD,
  checkInterval = DEFAULT_CHECK_INTERVAL
}: UseVoiceInterruptionOptions) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const consecutiveCountRef = useRef(0);

  const checkMicrophoneLevel = useCallback(() => {
    if (!analyser || !audioContext || audioContext.state !== 'running') {
      return;
    }

    // Get current volume level
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    // Calculate average volume
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      sum += dataArray[i];
    }
    const average = sum / bufferLength;

    // Check if volume exceeds threshold
    if (average > threshold) {
      consecutiveCountRef.current++;
      
      // Only trigger after consecutive loud samples to avoid false positives
      if (consecutiveCountRef.current >= CONSECUTIVE_THRESHOLD_COUNT) {
        console.log('🛑 Voice interruption detected! Volume:', average.toFixed(2));
        consecutiveCountRef.current = 0;
        onInterruption();
      }
    } else {
      // Reset counter if volume drops
      consecutiveCountRef.current = 0;
    }
  }, [analyser, audioContext, threshold, onInterruption]);

  useEffect(() => {
    // Only monitor when enabled and AI is speaking
    if (enabled && isSpeaking) {
      intervalRef.current = setInterval(checkMicrophoneLevel, checkInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        consecutiveCountRef.current = 0;
      };
    } else {
      // Clean up if not enabled or not speaking
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        consecutiveCountRef.current = 0;
      }
    }
  }, [enabled, isSpeaking, checkMicrophoneLevel, checkInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
};
